import React, { useState } from "react";
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Bell,
  Sparkles,
  Check,
  X,
  Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import GitHubIntegration from "../components/GitHubIntegration";
import OrganizationSettings from "../components/OrganizationSettings";
import { OrganizationProvider } from "../context/OrganizationContext";
import api from "../api/client";

function Settings() {
  const { user, setUser } = useAuth();
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);

  const toggleEmailNotifications = async () => {
    try {
      setUpdating(true);
      setMessage(null);

      const response = await api.post("/api/v1/users/notifications", {
        email_notifications: !user?.email_notifications,
      });

      setUser({
        ...user,
        email_notifications: response.data.email_notifications,
      });

      setMessage({
        type: "success",
        text: `Email notifications ${response.data.email_notifications ? "enabled" : "disabled"}!`,
      });
    } catch (error) {
      console.error("Error updating notifications:", error);
      setMessage({
        type: "error",
        text: "Failed to update notification settings",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <OrganizationProvider>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <SettingsIcon className="w-6 h-6 text-cyan-400" />
            <h2 className="text-2xl font-bold text-white">Settings</h2>
            <Sparkles className="w-4 h-4 text-cyan-400 ml-auto" />
          </div>
          <p className="text-gray-400">Manage your account and preferences</p>
        </div>

        <div className="grid gap-4">
          {/* Profile Section */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <User className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold">Profile</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-800/50">
                <span className="text-gray-400">Name</span>
                <span className="text-white">
                  {user?.full_name || "Not set"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-800/50">
                <span className="text-gray-400">Email</span>
                <span className="text-white">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-400">Member Since</span>
                <span className="text-white">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Organization Section */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold">Organization</h3>
            </div>
            <OrganizationSettings />
          </div>

          {/* GitHub Integration Section */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
            <GitHubIntegration />
          </div>

          {/* Security Section */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <Shield className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold">Security</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-800/50">
                <span className="text-gray-400">Change Password</span>
                <button className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                  Coming Soon
                </button>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-400">Two-Factor Authentication</span>
                <button className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                  Coming Soon
                </button>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <Bell className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold">Notifications</h3>
            </div>

            {/* Message */}
            {message && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm ${
                  message.type === "success"
                    ? "bg-green-500/10 border border-green-500/20 text-green-400"
                    : "bg-red-500/10 border border-red-500/20 text-red-400"
                }`}
              >
                {message.text}
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-800/50">
                <span className="text-gray-400">Email Notifications</span>
                <button
                  onClick={toggleEmailNotifications}
                  disabled={updating}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    user?.email_notifications
                      ? "bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
                      : "bg-gray-700/50 text-gray-400 border border-gray-600/50 hover:bg-gray-700/70"
                  } ${updating ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  {updating ? (
                    "Updating..."
                  ) : user?.email_notifications ? (
                    <>
                      <Check className="w-3 h-3" />
                      Enabled
                    </>
                  ) : (
                    <>
                      <X className="w-3 h-3" />
                      Disabled
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-400">Critical Error Alerts</span>
                <button className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                  Coming Soon
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OrganizationProvider>
  );
}

export default Settings;
